/**
 * ThrottledPromiseQueue handles running several promise-resolving functions at once, but
 * limited to a specified max number of executions at once.
 */

import { assert } from "./assert";

export const THROTTLED = '--throttled--';
const THROTTLED_WAIT = 1000;
const WAIT_BETWEEN_CALLS = 250;

export class ThrottledPromiseQueue {
  private fns: Array<() => Promise<void>> = [];
  private running = 0;
  private resolve!: () => void;
  private reject!: (reason?: any) => void;
  private rejected = false;
  private resolved = false;
  private started = false;
  private throttleTimer: any;
  private _numTotal: number = 0;
  private _numComplete: number = 0;
  private timers: any[] = [];

  constructor(readonly maxThreads: number) { }

  add(fn: () => Promise<void>) {
    this.fns.push(fn);
    this._numTotal++;
  }

  run(): Promise<void> {
    if (this.started) {
      return Promise.reject('ThrottledPromiseQueue.run() called twice.');
    }
    this.started = true;

    if (!this.fns.length) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      for (let i = 0; i < this.maxThreads && this.fns.length; i++) {
        this.timers.push(setTimeout(
          this.maybeStartNextPromise.bind(this), WAIT_BETWEEN_CALLS * i));
      }
    });
  }

  private async maybeStartNextPromise() {
    if (this.rejected) {
      return;
    }
    if (this.fns.length === 0) {
      if (this.running <= 0) {
        this.doResolve();
      }
      return;
    }

    const fn = this.fns.shift();
    assert(fn);
    this.running++;
    try {
      await fn();
      this.handlePromiseComplete();
    } catch (e: any) {
      this.running--;
      if (e === THROTTLED) {
        this.fns.unshift(fn);
        if (!this.throttleTimer) {
          this.throttleTimer = setTimeout(() => {
            this.throttleTimer = null;
            this.maybeStartNextPromise();
          }, THROTTLED_WAIT);
        }
      } else {
        this.handlePromiseReject(e);
      }
    }
  }

  private handlePromiseComplete() {
    this.running--;
    this._numComplete++;
    if (this.rejected) {
      return;
    }
    if (this.fns.length === 0) {
      if (this.running <= 0) {
        this.doResolve();
      }
      return;
    }
    this.timers.push(
      setTimeout(this.maybeStartNextPromise.bind(this), WAIT_BETWEEN_CALLS));
  }

  private doResolve() {
    if (this.resolved || this.rejected) {
      return;
    }
    clearTimeout(this.throttleTimer);
    this.resolved = true;
    this.resolve();
  }

  private handlePromiseReject(err?: any) {
    if (this.resolved || this.rejected) {
      return;
    }
    clearTimeout(this.throttleTimer);
    this.rejected = true;
    this.reject(err);
  }

  get numTotal(): number {
    return this._numTotal;
  }

  get numComplete(): number {
    return this._numComplete;
  }
}
